import * as THREE from 'three';

interface Num {
  el: HTMLDivElement;
  world: THREE.Vector3;
  t: number;
  life: number;
  dx: number;
  rise: number;
  scale: number;
  active: boolean;
}

export interface DamageNumberSpec {
  amount: number;
  kind: 'normal' | 'crit' | 'stag' | 'dot' | 'heal' | 'shield' | 'taken';
  back?: boolean;
  head?: boolean;
  counter?: boolean;
}

const fmt = new Intl.NumberFormat('en-US');

/**
 * Pooled DOM damage numbers projected from world space each frame. Only
 * transform/opacity change per frame (no layout thrash).
 */
export class DamageNumbers {
  private pool: Num[] = [];
  private tmp = new THREE.Vector3();
  /** Stacks rapid hits on the same target into a rising column. */
  private stack = new Map<number, { t: number; n: number }>();
  enabled = true;

  constructor(readonly root: HTMLElement) {}

  private acquire(): Num {
    const free = this.pool.find((n) => !n.active);
    if (free) return free;
    const el = document.createElement('div');
    el.className = 'dmg';
    this.root.appendChild(el);
    const n: Num = { el, world: new THREE.Vector3(), t: 0, life: 1, dx: 0, rise: 0, scale: 1, active: false };
    this.pool.push(n);
    return n;
  }

  spawn(targetId: number, pos: THREE.Vector3, spec: DamageNumberSpec, time: number): void {
    if (!this.enabled || this.pool.filter((n) => n.active).length > 140) return;
    const n = this.acquire();
    const st = this.stack.get(targetId);
    const idx = st && time - st.t < 0.25 ? st.n + 1 : 0;
    this.stack.set(targetId, { t: time, n: idx % 5 });
    n.active = true;
    n.world.copy(pos);
    n.t = 0;
    n.life = spec.kind === 'crit' ? 1.15 : spec.kind === 'dot' ? 0.8 : 0.95;
    n.dx = (Math.random() - 0.5) * 28;
    n.rise = idx * 22;
    n.scale = spec.kind === 'crit' ? 1 : 1;
    n.el.className = `dmg ${spec.kind === 'normal' ? '' : spec.kind}`;
    const tags: string[] = [];
    if (spec.counter) tags.push('<span class="tag counter">COUNTER</span>');
    if (spec.back) tags.push('<span class="tag back">BACK ATTACK</span>');
    if (spec.head) tags.push('<span class="tag head">HEAD ATTACK</span>');
    const prefix = spec.kind === 'heal' ? '+' : '';
    n.el.innerHTML = `${prefix}${fmt.format(Math.round(spec.amount))}${tags.join('')}`;
    n.el.style.opacity = '0';
  }

  update(dt: number, camera: THREE.Camera, w: number, h: number): void {
    for (const n of this.pool) {
      if (!n.active) continue;
      n.t += dt;
      if (n.t >= n.life) {
        n.active = false;
        n.el.style.opacity = '0';
        n.el.style.transform = 'translate(-9999px,0)';
        continue;
      }
      this.tmp.copy(n.world).project(camera);
      if (this.tmp.z > 1) {
        n.el.style.opacity = '0';
        continue;
      }
      const k = n.t / n.life;
      const x = (this.tmp.x * 0.5 + 0.5) * w + n.dx;
      const y = (-this.tmp.y * 0.5 + 0.5) * h - 30 - n.rise - k * 55;
      const punch = n.el.classList.contains('crit') ? 1 + 0.7 * Math.exp(-n.t * 14) : 1 + 0.25 * Math.exp(-n.t * 18);
      const a = k < 0.08 ? k / 0.08 : k > 0.7 ? 1 - (k - 0.7) / 0.3 : 1;
      n.el.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) translate(-50%, -50%) scale(${punch.toFixed(3)})`;
      n.el.style.opacity = a.toFixed(3);
    }
  }

  clear(): void {
    for (const n of this.pool) {
      n.active = false;
      n.el.style.opacity = '0';
    }
  }
}
